import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Loader2,
    LogOut,
    CheckCircle2,
    Clock,
    XCircle,
    Trash2,
    ChevronUp,
    ChevronDown,
    FileDown,
    NotebookPen,
    Heart,
    Users,
    ImageIcon,
    UploadCloud,
    FileText,
    Save,
    FileType,
    Megaphone,
    Presentation,
    KeyRound,
    Landmark,
    Calendar,
} from "lucide-react";
import { toast } from "sonner";
import {
    fetchMe,
    logout,
    adminChangePassword,
    adminListBlogs,
    adminApproveBlog,
    adminDeleteBlog,
    adminListIntentions,
    adminDeleteIntention,
    adminApprovePrayer,
    adminListGroupJoins,
    adminDeleteGroupJoin,
    adminGroupJoinsCsvUrl,
    fetchPhotos,
    adminUploadPhoto,
    adminDeletePhoto,
    adminUpdatePhotoCaption,
    fetchSiteContent,
    adminSaveSiteContent,
    fetchBulletins,
    adminUploadBulletin,
    adminDeleteBulletin,
    fetchAnnouncements,
    adminCreateAnnouncement,
    adminDeleteAnnouncement,
    fetchSlides,
    adminUploadSlide,
    adminDeleteSlide,
    adminReorderSlides,
    fetchSetting,
    adminUploadPoster,
    adminUploadCalendarPdf,
    fetchGroups,
    adminUpdateGroup,
    adminUploadGroupImage,
    adminUploadGroupMemberPhoto,
    adminDeleteGroupMemberPhoto,
    fetchEvents,
    adminCreateEvent,
    adminDeleteEvent,
} from "@/lib/api";
import { useContent } from "@/lib/ContentContext";

const formatDate = (d) => {
    try {
        return new Date(d).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (e) {
        return "";
    }
};

const Tab = ({ active, onClick, icon: Icon, label, count }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={`admin-tab-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
            active
                ? "bg-maroon-600 text-cream-50"
                : "text-maroon-700 hover:bg-cream-200"
        }`}
    >
        <Icon size={15} />
        {label}
        {typeof count === "number" && (
            <span className={`inline-flex items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${active ? "bg-cream-50 text-maroon-700" : "bg-maroon-600 text-cream-50"}`}>
                {count}
            </span>
        )}
    </button>
);

const Empty = ({ children }) => (
    <div className="rounded-2xl border border-dashed border-maroon-200 bg-cream-50 p-10 text-center text-maroon-700/80 text-sm">
        {children}
    </div>
);

// ---------------------------------------------------------------
// SiteContentEditor, grouped textareas for every human-editable
// string in the site. Loads from GET /api/content, saves with
// PUT /api/admin/content. Single Save button + "Saved ✓" indicator.
// ---------------------------------------------------------------
const CMS_SECTIONS = [
    { key: "header",   label: "Header",     fields: ["siteName","siteFullTitle","subtitle","archdiocese","motto","tagline","acronym","location"] },
    { key: "about",    label: "About",      fields: ["eyebrow","heading","mottoQuote","intro"] },
    { key: "values",   label: "Values",     fields: ["heading","intro"] },
    { key: "vision",   label: "Vision",     fields: ["heading","body"] },
    { key: "mission",  label: "Mission",    fields: ["heading","intro","closing","pledge"] },
    { key: "home",     label: "Home",       fields: ["heroEyebrow","heroTitleLine1","heroTitleLine2","heroTagline","heroWelcome","ctaMassTimes","ctaWatchLive","ctaPrayerWall"] },
    { key: "groups",   label: "Groups",     fields: ["pageEyebrow","pageTitle","pageIntro","sccHeading","sccIntro","ministriesHeading","ministriesIntro","associationsHeading","associationsIntro","sodalitiesHeading","sodalitiesIntro"] },
    { key: "events",   label: "Events",     fields: ["pageEyebrow","pageTitle","pageIntro","upcomingHeading","upcomingEmpty","pastHeading"] },
    { key: "footer",   label: "Footer & Tagline", fields: ["brand","brandLine2","brandLine3","mottoLine","taglineLine","copyright"] },
    { key: "contact",  label: "Footer & Contact",
                       fields: ["deaneryLine","addressLine1","addressLine2","phone","phone2","emailInfo","emailPriest"] },
    { key: "paybill",  label: "Church Paybill (Homepage Card)",
                       fields: ["heading","paybillNumber","accountLabelTithe","accountLabelOthers","note"] },
    { key: "confession", label: "Sacrament of Confession",
                       fields: ["line"] },
    { key: "intentions", label: "Mass Intentions",
                       fields: ["heading","body"] },
];

const humanize = (s) => s.replace(/([A-Z0-9])/g, " $1").replace(/^./, (c) => c.toUpperCase()).replace(/\s+/g, " ").trim();

const SiteContentEditor = () => {
    const { content: currentContent, refresh } = useContent();
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        let alive = true;
        // Deep-merge server response over the currently-live content so every field the
        // editor renders is pre-filled, otherwise a fresh backend row returns "{}" and
        // the editor looks scarily blank even though the site is showing the defaults.
        const deepMerge = (base, ov) => {
            if (Array.isArray(base) || Array.isArray(ov)) return (ov ?? base);
            if (base && typeof base === "object" && ov && typeof ov === "object") {
                const out = { ...base };
                for (const k of Object.keys(ov)) out[k] = deepMerge(base[k], ov[k]);
                return out;
            }
            return ov ?? base;
        };
        fetchSiteContent()
            .then((doc) => { if (alive) setState(deepMerge(currentContent, doc || {})); })
            .catch(() => { if (alive) setState(currentContent); })
            .finally(() => { if (alive) setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const patch = (section, field, value) => {
        setState((prev) => ({ ...prev, [section]: { ...(prev?.[section] || {}), [field]: value } }));
    };

    const patchMission = (idx, value) => {
        setState((prev) => {
            const arr = [...(prev?.mission?.commitments || [])];
            arr[idx] = value;
            return { ...prev, mission: { ...(prev?.mission || {}), commitments: arr } };
        });
    };

    const patchClergyGroup = (group, idx, key, value) => {
        setState((prev) => {
            const list = [...((prev?.clergy?.[group]) || [])];
            list[idx] = { ...(list[idx] || {}), [key]: value };
            return { ...prev, clergy: { ...(prev?.clergy || {}), [group]: list } };
        });
    };

    const patchListItem = (section, listKey, idx, field, value) => {
        setState((prev) => {
            const list = [...((prev?.[section]?.[listKey]) || [])];
            list[idx] = { ...(list[idx] || {}), [field]: value };
            return { ...prev, [section]: { ...(prev?.[section] || {}), [listKey]: list } };
        });
    };

    const patchValuesPillar = (idx, field, value) => {
        setState((prev) => {
            const list = [...((prev?.values?.pillars) || [])];
            list[idx] = { ...(list[idx] || {}), [field]: value };
            return { ...prev, values: { ...(prev?.values || {}), pillars: list } };
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            await adminSaveSiteContent(state);
            setSavedAt(new Date());
            toast.success("Site content saved.");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save.");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !state) {
        return <Empty><Loader2 size={20} className="inline animate-spin mr-2" /> Loading site content…</Empty>;
    }

    return (
        <div className="space-y-6" data-testid="site-content-editor">
            <div className="rounded-2xl border border-maroon-100 bg-cream-50 p-4 flex items-center gap-4">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="site-content-save"
                    className="btn-primary"
                >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save all changes
                </button>
                {savedAt && (
                    <span data-testid="site-content-saved" className="text-sm inline-flex items-center gap-1 text-maroon-700">
                        <CheckCircle2 size={14} /> Saved ✓ at {savedAt.toLocaleTimeString()}
                    </span>
                )}
                <span className="text-xs italic text-maroon-700/70 ml-auto">
                    Edits take effect immediately for visitors, no rebuild required.
                </span>
            </div>

            {/* Flat-field sections */}
            {CMS_SECTIONS.map((sec) => (
                <details key={sec.key} open className="rounded-2xl border border-maroon-100 bg-cream-50 p-4">
                    <summary className="cursor-pointer font-serif text-lg text-maroon-800">{sec.label}</summary>
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                        {sec.fields.map((f) => {
                            const val = state[sec.key]?.[f] ?? "";
                            const isLong = String(val).length > 80 || /(intro|welcome|body|closing|pledge|description|Line2|Line3|Empty)/i.test(f);
                            return (
                                <label key={f} className={`block ${isLong ? "md:col-span-2" : ""}`}>
                                    <span className="block text-xs font-semibold text-maroon-800">{humanize(f)}</span>
                                    {isLong ? (
                                        <textarea
                                            rows={3}
                                            value={val}
                                            onChange={(e) => patch(sec.key, f, e.target.value)}
                                            data-testid={`cms-${sec.key}-${f}`}
                                            className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm"
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={val}
                                            onChange={(e) => patch(sec.key, f, e.target.value)}
                                            data-testid={`cms-${sec.key}-${f}`}
                                            className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm"
                                        />
                                    )}
                                </label>
                            );
                        })}
                    </div>
                </details>
            ))}

            {/* Values Pillars */}
            <details open className="rounded-2xl border border-maroon-100 bg-cream-50 p-4">
                <summary className="cursor-pointer font-serif text-lg text-maroon-800">Values Pillars (Love · Unity · Service)</summary>
                <div className="mt-4 space-y-4">
                    {(state.values?.pillars || []).map((p, i) => (
                        <div key={i} className="rounded-xl border border-maroon-100 bg-white p-3">
                            <label className="block text-xs font-semibold text-maroon-800">Title
                                <input type="text" value={p.title || ""} onChange={(e) => patchValuesPillar(i, "title", e.target.value)}
                                    data-testid={`cms-pillar-${i}-title`}
                                    className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                            </label>
                            {(p.points || []).map((pt, j) => (
                                <label key={j} className="block mt-2 text-xs font-semibold text-maroon-800">Point {j + 1}
                                    <input type="text" value={pt} onChange={(e) => {
                                        const newPoints = [...(p.points || [])];
                                        newPoints[j] = e.target.value;
                                        patchValuesPillar(i, "points", newPoints);
                                    }}
                                        data-testid={`cms-pillar-${i}-point-${j}`}
                                        className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                                </label>
                            ))}
                        </div>
                    ))}
                </div>
            </details>

            {/* Mission commitments */}
            <details open className="rounded-2xl border border-maroon-100 bg-cream-50 p-4">
                <summary className="cursor-pointer font-serif text-lg text-maroon-800">Mission, Commitments (bullets)</summary>
                <div className="mt-4 space-y-2">
                    {(state.mission?.commitments || []).map((c, i) => (
                        <textarea
                            key={i}
                            rows={2}
                            value={c}
                            onChange={(e) => patchMission(i, e.target.value)}
                            data-testid={`cms-mission-commitment-${i}`}
                            className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm"
                        />
                    ))}
                </div>
            </details>

            {/* Clergy */}
            <details open className="rounded-2xl border border-maroon-100 bg-cream-50 p-4">
                <summary className="cursor-pointer font-serif text-lg text-maroon-800">Clergy (names, roles &amp; image paths)</summary>
                {["currentParishPriest","currentlyServing","formerParishPriests","formerServing"].map((g) => (
                    <div key={g} className="mt-4">
                        <h4 className="text-sm font-semibold text-maroon-800">{humanize(g)}</h4>
                        {(state.clergy?.[g] || []).map((c, i) => (
                            <div key={i} className="mt-2 grid gap-2 md:grid-cols-3 rounded-lg border border-maroon-100 bg-white p-3">
                                <input type="text" value={c.name || ""} onChange={(e) => patchClergyGroup(g, i, "name", e.target.value)}
                                    placeholder="Name" data-testid={`cms-clergy-${g}-${i}-name`}
                                    className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                                <input type="text" value={c.role || ""} onChange={(e) => patchClergyGroup(g, i, "role", e.target.value)}
                                    placeholder="Role" data-testid={`cms-clergy-${g}-${i}-role`}
                                    className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                                <input type="text" value={c.image || ""} onChange={(e) => patchClergyGroup(g, i, "image", e.target.value)}
                                    placeholder="Image path" data-testid={`cms-clergy-${g}-${i}-image`}
                                    className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                            </div>
                        ))}
                    </div>
                ))}
            </details>

            {/* Mass Times */}
            <details open className="rounded-2xl border border-maroon-100 bg-cream-50 p-4">
                <summary className="cursor-pointer font-serif text-lg text-maroon-800">Mass Times</summary>
                {[
                    ["sunday",   "Sunday"],
                    ["weekday",  "Weekday (Mon–Fri)"],
                    ["saturday", "Saturday"],
                ].map(([k, lbl]) => (
                    <div key={k} className="mt-4">
                        <h4 className="text-sm font-semibold text-maroon-800">{lbl}</h4>
                        {(state.massTimes?.[k] || []).map((row, i) => (
                            <div key={i} className="mt-2 grid gap-2 grid-cols-[140px_1fr] rounded-lg border border-maroon-100 bg-white p-3">
                                <input type="text" value={row.time || ""} onChange={(e) => patchListItem("massTimes", k, i, "time", e.target.value)}
                                    data-testid={`cms-mass-${k}-${i}-time`}
                                    className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                                <input type="text" value={row.note || ""} onChange={(e) => patchListItem("massTimes", k, i, "note", e.target.value)}
                                    data-testid={`cms-mass-${k}-${i}-note`}
                                    className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                            </div>
                        ))}
                    </div>
                ))}
                <div className="mt-4 grid gap-2 md:grid-cols-2">
                    <label className="block text-xs font-semibold text-maroon-800">Confession note
                        <textarea rows={2} value={state.massTimes?.confession || ""} onChange={(e) => patch("massTimes", "confession", e.target.value)}
                            data-testid="cms-mass-confession"
                            className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                    </label>
                    <label className="block text-xs font-semibold text-maroon-800">Obligation note
                        <textarea rows={2} value={state.massTimes?.obligation || ""} onChange={(e) => patch("massTimes", "obligation", e.target.value)}
                            data-testid="cms-mass-obligation"
                            className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                    </label>
                </div>
            </details>

            {/* Groups collections */}
            <details open className="rounded-2xl border border-maroon-100 bg-cream-50 p-4">
                <summary className="cursor-pointer font-serif text-lg text-maroon-800">Ministries · Associations · Sodalities</summary>
                {[
                    ["ministries",  "Ministries"],
                    ["associations","Associations"],
                    ["sodalities",  "Sodalities"],
                ].map(([k, lbl]) => (
                    <div key={k} className="mt-4">
                        <h4 className="text-sm font-semibold text-maroon-800">{lbl}</h4>
                        {(state.groups?.[k] || []).map((row, i) => (
                            <div key={i} className="mt-2 grid gap-2 md:grid-cols-[100px_1fr] rounded-lg border border-maroon-100 bg-white p-3">
                                <input type="text" value={row.code || ""} onChange={(e) => patchListItem("groups", k, i, "code", e.target.value)}
                                    placeholder="Code" data-testid={`cms-groups-${k}-${i}-code`}
                                    className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                                <div className="grid gap-2">
                                    <input type="text" value={row.name || ""} onChange={(e) => patchListItem("groups", k, i, "name", e.target.value)}
                                        placeholder="Name" data-testid={`cms-groups-${k}-${i}-name`}
                                        className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                                    <textarea rows={3} value={row.description || ""} onChange={(e) => patchListItem("groups", k, i, "description", e.target.value)}
                                        placeholder="Description" data-testid={`cms-groups-${k}-${i}-desc`}
                                        className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
                <div className="mt-4">
                    <h4 className="text-sm font-semibold text-maroon-800">SCC Names</h4>
                    {(state.groups?.sccs || []).map((s, i) => (
                        <input key={i} type="text" value={s.name || ""} onChange={(e) => patchListItem("groups", "sccs", i, "name", e.target.value)}
                            data-testid={`cms-scc-${i}-name`}
                            className="mt-2 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                    ))}
                </div>
            </details>

            {/* Past events */}
            <details open className="rounded-2xl border border-maroon-100 bg-cream-50 p-4">
                <summary className="cursor-pointer font-serif text-lg text-maroon-800">Past Events</summary>
                {(state.events?.past || []).map((e, i) => (
                    <div key={e.id || i} className="mt-3 grid gap-2 rounded-lg border border-maroon-100 bg-white p-3">
                        <input type="text" value={e.title || ""} onChange={(ev) => patchListItem("events", "past", i, "title", ev.target.value)}
                            placeholder="Title" data-testid={`cms-past-event-${i}-title`}
                            className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                        <textarea rows={3} value={e.description || ""} onChange={(ev) => patchListItem("events", "past", i, "description", ev.target.value)}
                            placeholder="Description" data-testid={`cms-past-event-${i}-desc`}
                            className="rounded-lg border border-maroon-200 bg-white px-2 py-1.5 text-sm" />
                    </div>
                ))}
            </details>

            {/* Save at bottom too */}
            <div className="pt-2">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="site-content-save"
                    className="btn-primary"
                >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save all changes
                </button>
                {savedAt && (
                    <span className="ml-3 text-sm inline-flex items-center gap-1 text-maroon-700">
                        <CheckCircle2 size={14} /> Saved ✓
                    </span>
                )}
            </div>
        </div>
    );
};
/* Inline caption editor for each photo in the Admin → Photos grid. */
const PhotoCaptionEditor = ({ photo, onSaved }) => {
    const [caption, setCaption] = useState(photo.caption || "");
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await adminUpdatePhotoCaption(photo.id, caption);
            toast.success("Caption updated.");
            setEditing(false);
            if (onSaved) await onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.error || "Could not update caption.");
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <div>
                <p className="text-xs text-maroon-700/80 min-h-[2.5rem]">
                    {photo.caption || <span className="italic text-maroon-700/50">No caption</span>}
                </p>
                <button
                    type="button"
                    onClick={() => setEditing(true)}
                    data-testid={`admin-photo-edit-${photo.id}`}
                    className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gold-500 hover:text-maroon-700"
                >
                    Edit caption
                </button>
            </div>
        );
    }
    return (
        <div className="space-y-2">
            <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={2}
                maxLength={280}
                className="w-full text-xs rounded-md border border-maroon-200 bg-white px-2 py-1"
                data-testid={`admin-photo-caption-input-${photo.id}`}
            />
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid={`admin-photo-caption-save-${photo.id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-maroon-600 hover:bg-maroon-700 text-cream-50 px-2 py-1 rounded"
                >
                    {saving ? <Loader2 size={10} className="animate-spin" /> : null} Save
                </button>
                <button
                    type="button"
                    onClick={() => { setCaption(photo.caption || ""); setEditing(false); }}
                    className="text-[10px] uppercase tracking-wider text-maroon-700 underline"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};



/* SlideOrderBoard, groups slides by context (home / about), lets the admin
 * move each slide up or down. Reorder is committed per context so the two
 * slideshows stay independent.
 *
 * onReorder receives the ordered list of slide IDs for the whole set (both
 * contexts merged); the backend endpoint updates sort_order on each id. */
const SlideOrderBoard = ({ slides, onRemove, onReorder }) => {
    const byContext = { home: [], about: [] };
    for (const s of slides) {
        const ctx = s.context || "home";
        (byContext[ctx] || (byContext[ctx] = [])).push(s);
    }
    // Ensure current order sort within each context, in case backend returned mixed
    for (const k of Object.keys(byContext)) {
        byContext[k] = [...byContext[k]].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    }

    const move = (ctx, index, delta) => {
        const list = [...byContext[ctx]];
        const j = index + delta;
        if (j < 0 || j >= list.length) return;
        [list[index], list[j]] = [list[j], list[index]];
        // Merge across contexts, keeping the OTHER context's order untouched.
        const otherKeys = Object.keys(byContext).filter((k) => k !== ctx);
        const merged = [
            ...list,
            ...otherKeys.flatMap((k) => byContext[k]),
        ];
        onReorder(merged.map((s) => s.id));
    };

    const renderBlock = (ctx, label) => {
        const list = byContext[ctx];
        if (!list || list.length === 0) return null;
        return (
            <div key={ctx} className="mb-6">
                <h4 className="text-xs font-semibold uppercase tracking-widest text-maroon-700 mb-2">
                    {label} ({list.length})
                </h4>
                <div className="grid gap-3 md:grid-cols-3">
                    {list.map((s, i) => (
                        <article key={s.id} data-testid={`admin-slide-${s.id}`} className="rounded-2xl border border-maroon-100 bg-cream-50 p-3">
                            <img src={s.url} alt={s.caption || ""} className="w-full h-40 object-cover rounded-lg" />
                            {s.caption && <p className="text-xs text-maroon-700/80 italic mt-2">{s.caption}</p>}
                            <div className="mt-2 text-[10px] uppercase tracking-widest text-maroon-700/60 font-semibold">
                                Position {i + 1}{s.bundled ? " · Bundled" : ""}
                            </div>
                            <div className="mt-2 grid grid-cols-3 gap-1">
                                <button
                                    onClick={() => move(ctx, i, -1)}
                                    disabled={i === 0}
                                    data-testid={`admin-slide-up-${s.id}`}
                                    aria-label="Move slide up"
                                    className="inline-flex items-center justify-center gap-1 rounded-full border border-maroon-200 text-maroon-700 hover:bg-maroon-100 disabled:opacity-40 px-2 py-1.5 text-xs font-semibold"
                                >
                                    <ChevronUp size={14} />
                                </button>
                                <button
                                    onClick={() => move(ctx, i, 1)}
                                    disabled={i === list.length - 1}
                                    data-testid={`admin-slide-down-${s.id}`}
                                    aria-label="Move slide down"
                                    className="inline-flex items-center justify-center gap-1 rounded-full border border-maroon-200 text-maroon-700 hover:bg-maroon-100 disabled:opacity-40 px-2 py-1.5 text-xs font-semibold"
                                >
                                    <ChevronDown size={14} />
                                </button>
                                <button
                                    onClick={() => onRemove(s.id)}
                                    data-testid={`admin-slide-remove-${s.id}`}
                                    aria-label="Remove slide"
                                    className="inline-flex items-center justify-center gap-1 rounded-full border border-maroon-200 text-maroon-700 hover:bg-maroon-100 px-2 py-1.5 text-xs font-semibold"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div>
            {renderBlock("home", "Homepage Hero")}
            {renderBlock("about", "About Page")}
        </div>
    );
};



const AdminDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [me, setMe] = useState(location.state?.user || null);
    const [authChecking, setAuthChecking] = useState(!location.state?.user);
    const [tab, setTab] = useState("blogs-pending");

    const [pendingBlogs, setPendingBlogs] = useState([]);
    const [approvedBlogs, setApprovedBlogs] = useState([]);
    const [intentions, setIntentions] = useState([]);
    const [joins, setJoins] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [bulletins, setBulletins] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [slides, setSlides] = useState([]);
    const [tithePoster, setTithePoster] = useState(null);
    const [annexPoster, setAnnexPoster] = useState(null);
    const [calendarPoster, setCalendarPoster] = useState(null);
    const [calendarPdf, setCalendarPdf] = useState(null);
    const [groups, setGroups] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);

    // Photo upload state
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadCaption, setUploadCaption] = useState("");
    const [uploading, setUploading] = useState(false);

    // Verify auth via /auth/me (server-side source of truth)
    useEffect(() => {
        if (me) return;
        fetchMe()
            .then((u) => setMe(u))
            .catch(() => navigate("/admin/login", { replace: true }))
            .finally(() => setAuthChecking(false));
    }, [me, navigate]);

    const reload = async () => {
        setLoading(true);
        try {
            const [p, a, i, j, ph, bul, ann, sl, tp, apPoster, calPoster, calPdf, gr, ev] = await Promise.all([
                adminListBlogs("pending"),
                adminListBlogs("approved"),
                adminListIntentions(),
                adminListGroupJoins(),
                fetchPhotos(),
                fetchBulletins().catch(() => []),
                fetchAnnouncements(50).catch(() => []),
                fetchSlides("home").catch(() => []).then(async (home) => {
                    const about = await fetchSlides("about").catch(() => []);
                    return [...home.map(s => ({ ...s, context: "home" })), ...about.map(s => ({ ...s, context: "about" }))];
                }),
                fetchSetting("tithe_poster").catch(() => null),
                fetchSetting("project_annex").catch(() => null),
                fetchSetting("calendar_poster").catch(() => null),
                fetchSetting("calendar_pdf").catch(() => null),
                fetchGroups().catch(() => []),
                fetchEvents().catch(() => []),
            ]);
            setPendingBlogs(p);
            setApprovedBlogs(a);
            setIntentions(i);
            setJoins(j);
            setPhotos(ph);
            setBulletins(bul);
            setAnnouncements(ann);
            setSlides(sl);
            setTithePoster(tp);
            setAnnexPoster(apPoster);
            setCalendarPoster(calPoster);
            setCalendarPdf(calPdf);
            setGroups(gr);
            setEvents(ev);
        } catch (e) {
            if (e?.response?.status === 401 || e?.response?.status === 403) {
                navigate("/admin/login", { replace: true });
                return;
            }
            toast.error("Could not load admin data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (me) reload();
    }, [me]);

    const onLogout = async () => {
        try {
            await logout();
        } catch (e) { /* ignore */ }
        navigate("/admin/login", { replace: true });
    };

    const approveBlog = async (id) => {
        try {
            await adminApproveBlog(id);
            toast.success("Blog approved and published.");
            await reload();
        } catch (e) {
            toast.error("Could not approve.");
        }
    };

    const deleteBlog = async (id) => {
        if (!window.confirm("Permanently delete this blog post?")) return;
        try {
            await adminDeleteBlog(id);
            toast.success("Blog deleted.");
            await reload();
        } catch (e) {
            toast.error("Could not delete.");
        }
    };

    const deleteIntention = async (id) => {
        if (!window.confirm("Delete this prayer intention?")) return;
        try {
            await adminDeleteIntention(id);
            toast.success("Intention removed.");
            await reload();
        } catch (e) {
            toast.error("Could not delete.");
        }
    };

    const deleteJoin = async (id) => {
        if (!window.confirm("Delete this group-join request?")) return;
        try {
            await adminDeleteGroupJoin(id);
            toast.success("Request removed.");
            await reload();
        } catch (e) {
            toast.error("Could not delete.");
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!uploadFile) {
            toast.error("Please choose a photo.");
            return;
        }
        if (uploadFile.size > 8 * 1024 * 1024) {
            toast.error("Photo too large. Max 8 MB.");
            return;
        }
        setUploading(true);
        try {
            await adminUploadPhoto(uploadFile, uploadCaption);
            toast.success("Photo uploaded, it's live on the gallery.");
            setUploadFile(null);
            setUploadCaption("");
            // reset the file input
            const inp = document.getElementById("admin-photo-input");
            if (inp) inp.value = "";
            await reload();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(detail || "Upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const deletePhoto = async (id) => {
        if (!window.confirm("Permanently delete this photo?")) return;
        try {
            await adminDeletePhoto(id);
            toast.success("Photo removed.");
            await reload();
        } catch (e) {
            toast.error("Could not delete.");
        }
    };

    // ---- Prayer intention moderation ----
    const approveIntention = async (id) => {
        try {
            await adminApprovePrayer(id, true);
            toast.success("Intention approved, now visible on the Prayer Wall.");
            await reload();
        } catch { toast.error("Could not approve."); }
    };
    const rejectIntention = async (id) => {
        try {
            await adminApprovePrayer(id, false);
            toast.success("Intention hidden from the Prayer Wall.");
            await reload();
        } catch { toast.error("Could not update."); }
    };

    // ---- Bulletins ----
    const [bulletinFile, setBulletinFile]   = useState(null);
    const [bulletinTitle, setBulletinTitle] = useState("");
    const [bulletinYear, setBulletinYear]   = useState(new Date().getFullYear());
    const [uploadingBul, setUploadingBul]   = useState(false);

    const handleBulletinUpload = async (e) => {
        e.preventDefault();
        if (!bulletinFile) { toast.error("Choose a PDF first."); return; }
        if (!bulletinTitle.trim()) { toast.error("Give the bulletin a title."); return; }
        setUploadingBul(true);
        try {
            await adminUploadBulletin(bulletinFile, { title: bulletinTitle.trim(), year: bulletinYear });
            toast.success("Bulletin uploaded.");
            setBulletinFile(null); setBulletinTitle(""); setBulletinYear(new Date().getFullYear());
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.error || "Upload failed.");
        } finally { setUploadingBul(false); }
    };

    const deleteBulletin = async (id) => {
        if (!window.confirm("Permanently delete this bulletin PDF?")) return;
        try { await adminDeleteBulletin(id); toast.success("Bulletin removed."); await reload(); }
        catch { toast.error("Could not delete."); }
    };

    // ---- Announcements ----
    const [annTitle, setAnnTitle] = useState("");
    const [annBody, setAnnBody] = useState("");
    const [annFile, setAnnFile] = useState(null);
    const [annSubmitting, setAnnSubmitting] = useState(false);
    const handleAnnSubmit = async (e) => {
        e.preventDefault();
        if (!annTitle.trim() || !annBody.trim()) { toast.error("Title and body required."); return; }
        setAnnSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("title", annTitle.trim());
            fd.append("body", annBody.trim());
            if (annFile) fd.append("file", annFile);
            await adminCreateAnnouncement(fd);
            toast.success("Announcement published.");
            setAnnTitle(""); setAnnBody(""); setAnnFile(null);
            await reload();
        } catch (err) { toast.error(err?.response?.data?.error || "Failed to publish."); }
        finally { setAnnSubmitting(false); }
    };
    const removeAnnouncement = async (id) => {
        if (!window.confirm("Delete this announcement?")) return;
        try { await adminDeleteAnnouncement(id); toast.success("Removed."); await reload(); }
        catch { toast.error("Could not delete."); }
    };

    // ---- Slideshow ----
    const [slideFile, setSlideFile] = useState(null);
    const [slideCaption, setSlideCaption] = useState("");
    const [slideContext, setSlideContext] = useState("home");
    const [slideUploading, setSlideUploading] = useState(false);
    const handleSlideUpload = async (e) => {
        e.preventDefault();
        if (!slideFile) { toast.error("Choose an image first."); return; }
        setSlideUploading(true);
        try {
            await adminUploadSlide(slideFile, slideCaption.trim(), slideContext);
            toast.success("Slide added.");
            setSlideFile(null); setSlideCaption("");
            await reload();
        } catch (err) { toast.error(err?.response?.data?.error || "Upload failed."); }
        finally { setSlideUploading(false); }
    };
    const removeSlide = async (id) => {
        if (!window.confirm("Delete this slide?")) return;
        try { await adminDeleteSlide(id); toast.success("Slide removed."); await reload(); }
        catch { toast.error("Could not delete."); }
    };

    // ---- Posters ----
    // Generic handler works for all three image poster keys.
    const [posterFiles, setPosterFiles] = useState({});
    const [posterBusy, setPosterBusy] = useState("");
    const handleUploadKey = (key, successLabel) => async (e) => {
        e.preventDefault();
        const file = posterFiles[key];
        if (!file) { toast.error("Choose an image first."); return; }
        setPosterBusy(key);
        try {
            await adminUploadPoster(key, file);
            toast.success(`${successLabel} updated.`);
            setPosterFiles((p) => ({ ...p, [key]: null }));
            await reload();
        } catch (err) { toast.error(err?.response?.data?.error || "Upload failed."); }
        finally { setPosterBusy(""); }
    };
    // Calendar PDF handler (separate endpoint, accepts .pdf)
    const [calendarPdfFile, setCalendarPdfFile] = useState(null);
    const [calendarPdfBusy, setCalendarPdfBusy] = useState(false);
    const handleCalendarPdfUpload = async (e) => {
        e.preventDefault();
        if (!calendarPdfFile) { toast.error("Choose a PDF first."); return; }
        setCalendarPdfBusy(true);
        try {
            await adminUploadCalendarPdf(calendarPdfFile);
            toast.success("Parish Calendar PDF updated.");
            setCalendarPdfFile(null);
            await reload();
        } catch (err) { toast.error(err?.response?.data?.error || "Upload failed."); }
        finally { setCalendarPdfBusy(false); }
    };

    // ---- Password change ----
    const [pwCurrent, setPwCurrent] = useState("");
    const [pwNew, setPwNew] = useState("");
    const [pwConfirm, setPwConfirm] = useState("");
    const [pwSaving, setPwSaving] = useState(false);
    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (pwNew.length < 8) { toast.error("New password must be at least 8 characters."); return; }
        if (pwNew !== pwConfirm) { toast.error("Passwords do not match."); return; }
        setPwSaving(true);
        try {
            await adminChangePassword(pwCurrent, pwNew);
            toast.success("Password changed. Please log in again with your new password.");
            setPwCurrent(""); setPwNew(""); setPwConfirm("");
            setTimeout(() => { logout(); navigate("/admin/login", { replace: true }); }, 1500);
        } catch (err) {
            toast.error(err?.response?.data?.error || "Could not change password.");
        } finally { setPwSaving(false); }
    };

    if (authChecking) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 size={28} className="animate-spin text-maroon-600" />
            </div>
        );
    }

    if (!me) return null;

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                    <span className="eyebrow">Parish Admin</span>
                    <h1 className="heading-section mt-1">Moderation Dashboard</h1>
                    <p className="text-xs text-maroon-700/70 mt-1">Signed in as <span className="font-semibold">{me.email}</span></p>
                </div>
                <button onClick={onLogout} data-testid="admin-logout" className="btn-ghost">
                    <LogOut size={14} /> Sign out
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6">
                <Tab active={tab === "blogs-pending"} onClick={() => setTab("blogs-pending")} icon={NotebookPen} label="Pending Blogs" count={pendingBlogs.length} />
                <Tab active={tab === "blogs-approved"} onClick={() => setTab("blogs-approved")} icon={NotebookPen} label="Approved Blogs" count={approvedBlogs.length} />
                <Tab active={tab === "intentions"} onClick={() => setTab("intentions")} icon={Heart} label="Prayer Intentions" count={intentions.length} />
                <Tab active={tab === "joins"} onClick={() => setTab("joins")} icon={Users} label="Group Join Requests" count={joins.length} />
                <Tab active={tab === "photos"} onClick={() => setTab("photos")} icon={ImageIcon} label="Photos" count={photos.length} />
                <Tab active={tab === "bulletins"} onClick={() => setTab("bulletins")} icon={FileType} label="Bulletins" count={bulletins.length} />
                <Tab active={tab === "announcements"} onClick={() => setTab("announcements")} icon={Megaphone} label="Announcements" count={announcements.length} />
                <Tab active={tab === "slideshow"} onClick={() => setTab("slideshow")} icon={Presentation} label="Slideshow" count={slides.length} />
                <Tab active={tab === "posters"} onClick={() => setTab("posters")} icon={Landmark} label="Posters" />
                <Tab active={tab === "groups"} onClick={() => setTab("groups")} icon={Users} label="Groups & SCCs" count={groups.length} />
                <Tab active={tab === "events"} onClick={() => setTab("events")} icon={Calendar} label="Events" count={events.length} />
                <Tab active={tab === "site-content"} onClick={() => setTab("site-content")} icon={FileText} label="Site Content" />
                <Tab active={tab === "account"} onClick={() => setTab("account")} icon={KeyRound} label="Account" />
                <a
                    href={adminGroupJoinsCsvUrl}
                    className="ml-auto inline-flex items-center gap-2 rounded-full bg-gold-400 hover:bg-gold-500 text-maroon-900 px-4 py-2 text-sm font-semibold"
                    data-testid="admin-export-csv"
                >
                    <FileDown size={14} /> Export Group-Joins CSV
                </a>
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-sm text-maroon-700/70 mb-4">
                    <Loader2 size={14} className="animate-spin" /> Refreshing…
                </div>
            )}

            {tab === "blogs-pending" && (
                <div className="grid gap-4">
                    {pendingBlogs.length === 0 && <Empty>No pending blogs. Anything new will land here for approval.</Empty>}
                    {pendingBlogs.map((b) => (
                        <article key={b.id} data-testid={`admin-blog-pending-${b.id}`} className="rounded-2xl border p-5" style={{ background: "#383838", borderColor: "#D4AF37", borderLeftWidth: 4 }}>
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold flex flex-wrap items-center gap-2" style={{ color: "#D4AF37" }}>
                                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5" style={{ background: "rgba(212, 175, 55, 0.15)", border: "1px solid #D4AF37" }}>
                                            <Clock size={10} /> Pending
                                        </span>
                                        <span style={{ color: "#CCCCCC" }}>{formatDate(b.created_at)} · {b.author}</span>
                                    </div>
                                    <h3 className="font-serif text-xl mt-2" style={{ color: "#FFFFFF" }}>{b.title}</h3>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => approveBlog(b.id)} data-testid={`admin-approve-${b.id}`} className="inline-flex items-center gap-1 rounded-full bg-maroon-600 hover:bg-maroon-700 text-cream-50 px-3 py-1.5 text-xs font-semibold">
                                        <CheckCircle2 size={14} /> Approve
                                    </button>
                                    <button onClick={() => deleteBlog(b.id)} data-testid={`admin-delete-${b.id}`} className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-white/5" style={{ borderColor: "#CCCCCC", color: "#CCCCCC" }}>
                                        <Trash2 size={14} /> Delete
                                    </button>
                                </div>
                            </div>
                            <div className="gold-divider my-3 opacity-40" />
                            <p className="text-sm italic" style={{ color: "#CCCCCC" }}>{b.excerpt}</p>
                            <pre className="text-sm leading-relaxed whitespace-pre-wrap font-sans mt-3 max-h-72 overflow-auto" style={{ color: "#F3EDE4" }}>
                                {b.body}
                            </pre>
                        </article>
                    ))}
                </div>
            )}

            {tab === "blogs-approved" && (
                <div className="grid gap-4">
                    {approvedBlogs.length === 0 && <Empty>No approved blogs yet.</Empty>}
                    {approvedBlogs.map((b) => (
                        <article key={b.id} className="rounded-2xl border p-5" style={{ background: "#252525", borderColor: "#2e7d32", borderLeftWidth: 4 }}>
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.18em] font-semibold flex flex-wrap items-center gap-2">
                                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5" style={{ background: "rgba(46, 125, 50, 0.22)", border: "1px solid #2e7d32", color: "#7dd58a" }}>
                                            <CheckCircle2 size={10} /> Approved
                                        </span>
                                        <span style={{ color: "#CCCCCC" }}>{formatDate(b.created_at)} · {b.author}</span>
                                    </div>
                                    <h3 className="font-serif text-lg mt-2" style={{ color: "#FFFFFF" }}>{b.title}</h3>
                                </div>
                                <button onClick={() => deleteBlog(b.id)} className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-white/5" style={{ borderColor: "#CCCCCC", color: "#CCCCCC" }}>
                                    <Trash2 size={14} /> Delete
                                </button>
                            </div>
                            <p className="text-sm mt-2" style={{ color: "#CCCCCC" }}>{b.excerpt}</p>
                        </article>
                    ))}
                </div>
            )}

            {tab === "intentions" && (
                <div className="grid gap-3 md:grid-cols-2">
                    {intentions.length === 0 && <Empty>No prayer intentions yet.</Empty>}
                    {intentions.map((it) => {
                        const pending = !it.approved;
                        return (
                            <article
                                key={it.id}
                                data-testid={`admin-intention-${it.id}`}
                                className="rounded-2xl border p-4"
                                style={{
                                    background: pending ? "#383838" : "#252525",
                                    borderColor: pending ? "#D4AF37" : "#2e7d32",
                                    borderLeftWidth: 4,
                                }}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-serif text-base flex items-center gap-2" style={{ color: "#FFFFFF" }}>
                                            {it.name || "Anonymous"}
                                            {pending ? (
                                                <span className="inline-flex items-center gap-1 rounded-full text-[10px] uppercase tracking-wider px-2 py-0.5 font-semibold"
                                                      style={{ background: "rgba(212, 175, 55, 0.15)", border: "1px solid #D4AF37", color: "#D4AF37" }}>
                                                    <Clock size={10} /> Pending
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full text-[10px] uppercase tracking-wider px-2 py-0.5 font-semibold"
                                                      style={{ background: "rgba(46, 125, 50, 0.22)", border: "1px solid #2e7d32", color: "#7dd58a" }}>
                                                    <CheckCircle2 size={10} /> Approved
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "#CCCCCC" }}>
                                            {formatDate(it.created_at)} · {it.pray_count || 0} prayers
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {pending ? (
                                            <button
                                                onClick={() => approveIntention(it.id)}
                                                data-testid={`admin-approve-intention-${it.id}`}
                                                className="inline-flex items-center gap-1 rounded-full bg-maroon-600 hover:bg-maroon-700 text-cream-50 px-3 py-1.5 text-xs font-semibold"
                                            >
                                                <CheckCircle2 size={14} /> Approve
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => rejectIntention(it.id)}
                                                data-testid={`admin-hide-intention-${it.id}`}
                                                className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-white/5"
                                                style={{ borderColor: "#CCCCCC", color: "#CCCCCC" }}
                                            >
                                                <XCircle size={14} /> Hide
                                            </button>
                                        )}
                                        <button
                                            onClick={() => deleteIntention(it.id)}
                                            data-testid={`admin-del-intention-${it.id}`}
                                            className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-white/5"
                                            style={{ borderColor: "#CCCCCC", color: "#CCCCCC" }}
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    </div>
                                </div>
                                <div className="gold-divider my-2 opacity-40" />
                                <p className="text-sm whitespace-pre-wrap" style={{ color: "#F3EDE4" }}>{it.intention}</p>
                            </article>
                        );
                    })}
                </div>
            )}

            {tab === "joins" && (
                <div
                    className="rounded-2xl border-l-4 shadow-lg overflow-hidden"
                    style={{
                        background: "#252525",
                        borderLeftColor: "#900000",
                    }}
                >
                    <div className="px-5 py-4 border-b" style={{ borderColor: "#3d3d3d" }}>
                        <h3 className="font-serif text-lg text-cream-50">Group Join Requests</h3>
                        <p className="text-xs text-cream-50/70 mt-1">Parishioners who signed up to join a ministry or Small Christian Community.</p>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr style={{ background: "#1a1a1a" }}>
                                    <th className="text-left p-3 text-[11px] uppercase tracking-wider text-gold-300">Date</th>
                                    <th className="text-left p-3 text-[11px] uppercase tracking-wider text-gold-300">Group</th>
                                    <th className="text-left p-3 text-[11px] uppercase tracking-wider text-gold-300">Name</th>
                                    <th className="text-left p-3 text-[11px] uppercase tracking-wider text-gold-300">Email</th>
                                    <th className="text-left p-3 text-[11px] uppercase tracking-wider text-gold-300">Phone</th>
                                    <th className="text-left p-3 text-[11px] uppercase tracking-wider text-gold-300">Message</th>
                                    <th className="text-right p-3 text-[11px] uppercase tracking-wider text-gold-300">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {joins.length === 0 && (
                                    <tr><td colSpan={7} className="p-8 text-center text-cream-50/60 italic">No join requests yet.</td></tr>
                                )}
                                {joins.map((j) => (
                                    <tr key={j.id} data-testid={`admin-join-${j.id}`} className="border-t align-top" style={{ borderColor: "#3d3d3d" }}>
                                        <td className="p-3 text-xs text-cream-50/70 whitespace-nowrap">{formatDate(j.created_at)}</td>
                                        <td className="p-3 font-semibold" style={{ color: "#D4AF37" }}>{j.group_name}</td>
                                        <td className="p-3 text-cream-50">{j.name}</td>
                                        <td className="p-3"><a className="underline decoration-gold-300 underline-offset-4 text-cream-50 hover:text-gold-300" href={`mailto:${j.email}`}>{j.email}</a></td>
                                        <td className="p-3 text-cream-50/90">{j.phone || ","}</td>
                                        <td className="p-3 max-w-xs text-cream-50/90">{j.message || ","}</td>
                                        <td className="p-3 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => deleteJoin(j.id)}
                                                className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold text-cream-50 hover:bg-maroon-800 transition-colors"
                                                style={{ borderColor: "#D4AF37" }}
                                            >
                                                <Trash2 size={14} /> Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "photos" && (
                <div className="space-y-6">
                    <form
                        onSubmit={handleUpload}
                        data-testid="admin-photo-upload-form"
                        className="rounded-2xl border border-maroon-100 bg-white/85 p-5 shadow-sm"
                    >
                        <div className="flex items-center gap-2">
                            <UploadCloud size={18} className="text-maroon-600" />
                            <h3 className="font-serif text-xl text-maroon-800">Upload a Photo</h3>
                        </div>
                        <div className="gold-divider my-3 opacity-50" />
                        <div className="grid gap-3 md:grid-cols-[1fr,1fr,auto] items-end">
                            <label className="block">
                                <span className="text-xs uppercase tracking-wider text-maroon-700/80">Image (JPG, PNG, WebP, max 8 MB)</span>
                                <input
                                    id="admin-photo-input"
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                    data-testid="admin-photo-input"
                                    className="mt-1 w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-maroon-600 file:text-cream-50 file:px-4 file:py-2 file:cursor-pointer hover:file:bg-maroon-700"
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs uppercase tracking-wider text-maroon-700/80">Caption (optional)</span>
                                <input
                                    type="text"
                                    value={uploadCaption}
                                    onChange={(e) => setUploadCaption(e.target.value)}
                                    maxLength={300}
                                    placeholder="e.g. Baptism celebration, Easter 2026"
                                    data-testid="admin-photo-caption"
                                    className="mt-1 w-full rounded-xl border border-maroon-200 bg-cream-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={uploading || !uploadFile}
                                data-testid="admin-photo-upload-submit"
                                className="btn-primary disabled:opacity-60 whitespace-nowrap"
                            >
                                {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />} Upload
                            </button>
                        </div>
                        <p className="text-[11px] text-maroon-700/70 italic mt-3">
                            Uploaded photos are auto-resized to a max width of 1920px and saved as WebP/PNG for fast loading. They appear immediately on the public Photos page.
                        </p>
                    </form>

                    {photos.length === 0 ? (
                        <Empty>No photos uploaded yet. The Gallery currently shows the built-in parish photos.</Empty>
                    ) : (
                        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                            {photos.map((p) => (
                                <figure
                                    key={p.id}
                                    data-testid={`admin-photo-${p.id}`}
                                    className="group relative overflow-hidden rounded-2xl border border-maroon-100 bg-cream-50"
                                >
                                    <img
                                        src={p.url}
                                        alt={p.caption || "Parish photo"}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-44 object-cover"
                                    />
                                    <figcaption className="p-3 space-y-2">
                                        <PhotoCaptionEditor photo={p} onSaved={reload} />
                                    </figcaption>
                                    <button
                                        type="button"
                                        onClick={() => deletePhoto(p.id)}
                                        data-testid={`admin-photo-delete-${p.id}`}
                                        className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full bg-maroon-900/85 text-cream-50 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-maroon-700"
                                        aria-label="Delete photo"
                                        title="Delete photo"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </figure>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "bulletins" && (
                <div className="space-y-6">
                    <form
                        onSubmit={handleBulletinUpload}
                        data-testid="admin-bulletin-upload-form"
                        className="rounded-2xl border border-maroon-100 bg-white/85 p-5 shadow-sm space-y-3"
                    >
                        <h3 className="font-serif text-lg text-maroon-800 flex items-center gap-2">
                            <UploadCloud size={18} className="text-gold-500" /> Upload a new bulletin (PDF)
                        </h3>
                        <div className="grid gap-3 md:grid-cols-2">
                            <label className="block">
                                <span className="text-xs font-semibold text-maroon-800">Title</span>
                                <input
                                    type="text"
                                    value={bulletinTitle}
                                    onChange={(e) => setBulletinTitle(e.target.value)}
                                    placeholder="e.g., COLUS Newsletter, Advent Edition"
                                    data-testid="admin-bulletin-title"
                                    className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm"
                                    required
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs font-semibold text-maroon-800">Year</span>
                                <input
                                    type="number"
                                    min="1980"
                                    max="2100"
                                    value={bulletinYear}
                                    onChange={(e) => setBulletinYear(e.target.value)}
                                    data-testid="admin-bulletin-year"
                                    className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm"
                                    required
                                />
                            </label>
                        </div>
                        <label className="block">
                            <span className="text-xs font-semibold text-maroon-800">PDF file</span>
                            <input
                                type="file"
                                accept="application/pdf,.pdf"
                                onChange={(e) => setBulletinFile(e.target.files?.[0] || null)}
                                data-testid="admin-bulletin-file"
                                className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm"
                                required
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={uploadingBul}
                            data-testid="admin-bulletin-submit"
                            className="btn-primary"
                        >
                            {uploadingBul ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload bulletin
                        </button>
                    </form>

                    {bulletins.length === 0 ? (
                        <Empty>No bulletins uploaded yet.</Empty>
                    ) : (
                        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                            {bulletins.map((b) => (
                                <article
                                    key={b.id}
                                    data-testid={`admin-bulletin-${b.id}`}
                                    className="rounded-2xl border border-maroon-100 bg-cream-50 p-4"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="h-10 w-10 rounded-lg bg-maroon-700 text-cream-50 flex items-center justify-center flex-shrink-0">
                                            <FileType size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <a
                                                href={b.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="font-serif text-maroon-800 hover:text-maroon-900 underline decoration-gold-400 underline-offset-4"
                                            >
                                                {b.title}
                                            </a>
                                            <p className="text-xs text-maroon-700/70 mt-1">
                                                {b.year} · {b.size_bytes ? Math.round(b.size_bytes / 1024) + " KB" : ","}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="gold-divider my-3 opacity-40" />
                                    <div className="flex items-center justify-end gap-2">
                                        <a
                                            href={b.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs font-semibold text-maroon-700 hover:text-maroon-900"
                                        >
                                            Open PDF →
                                        </a>
                                        <button
                                            onClick={() => deleteBulletin(b.id)}
                                            data-testid={`admin-bulletin-delete-${b.id}`}
                                            className="inline-flex items-center gap-1 rounded-full border border-maroon-200 text-maroon-700 hover:bg-maroon-100 px-3 py-1.5 text-xs font-semibold"
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "announcements" && (
                <div className="space-y-6">
                    <form onSubmit={handleAnnSubmit} className="rounded-2xl border border-maroon-100 bg-white/85 p-5 shadow-sm space-y-3">
                        <h3 className="font-serif text-lg text-maroon-800 flex items-center gap-2"><Megaphone size={18} className="text-gold-500" /> New announcement</h3>
                        <input type="text" value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} placeholder="Title" data-testid="admin-ann-title" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" required />
                        <textarea rows={4} value={annBody} onChange={(e) => setAnnBody(e.target.value)} placeholder="Announcement body" data-testid="admin-ann-body" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" required />
                        <input type="file" accept="image/*" onChange={(e) => setAnnFile(e.target.files?.[0] || null)} data-testid="admin-ann-file" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                        <button type="submit" disabled={annSubmitting} data-testid="admin-ann-submit" className="btn-primary">
                            {annSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Megaphone size={14} />} Publish announcement
                        </button>
                    </form>
                    {announcements.length === 0 ? <Empty>No announcements yet.</Empty> : (
                        <div className="grid gap-3 md:grid-cols-2">
                            {announcements.map((a) => (
                                <article key={a.id} data-testid={`admin-ann-${a.id}`} className="rounded-2xl border border-maroon-100 bg-cream-50 p-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div><h4 className="font-serif text-maroon-800">{a.title}</h4><div className="text-[11px] uppercase tracking-[0.18em] text-gold-500/80">{formatDate(a.published_at)}</div></div>
                                        <button onClick={() => removeAnnouncement(a.id)} className="inline-flex items-center gap-1 rounded-full border border-maroon-200 text-maroon-700 hover:bg-maroon-100 px-3 py-1.5 text-xs font-semibold"><Trash2 size={14} /> Delete</button>
                                    </div>
                                    {a.image_url && <img src={a.image_url} alt={a.title} className="mt-3 w-full h-40 object-cover rounded-lg" />}
                                    <p className="text-sm text-maroon-700/90 mt-3 whitespace-pre-wrap">{a.body}</p>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "slideshow" && (
                <div className="space-y-6">
                    <form onSubmit={handleSlideUpload} className="rounded-2xl border border-maroon-100 bg-white/85 p-5 shadow-sm space-y-3">
                        <h3 className="font-serif text-lg text-maroon-800 flex items-center gap-2"><Presentation size={18} className="text-gold-500" /> Add slide</h3>
                        <label className="block">
                            <span className="text-xs font-semibold text-maroon-800">Slideshow</span>
                            <select
                                value={slideContext}
                                onChange={(e) => setSlideContext(e.target.value)}
                                data-testid="admin-slide-context"
                                className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm"
                            >
                                <option value="home">Homepage Hero</option>
                                <option value="about">About Page</option>
                            </select>
                        </label>
                        <input type="text" value={slideCaption} onChange={(e) => setSlideCaption(e.target.value)} placeholder="Optional caption" data-testid="admin-slide-caption" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                        <input type="file" accept="image/*" onChange={(e) => setSlideFile(e.target.files?.[0] || null)} data-testid="admin-slide-file" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" required />
                        <button type="submit" disabled={slideUploading} data-testid="admin-slide-submit" className="btn-primary">
                            {slideUploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload slide
                        </button>
                        <p className="text-xs text-maroon-700/60 italic">If no slides are uploaded, the homepage falls back to the 8 bundled parish photos.</p>
                    </form>
                    {slides.length === 0 ? <Empty>No custom slides yet, bundled photos are showing on the homepage.</Empty> : (
                        <SlideOrderBoard
                            slides={slides}
                            onRemove={removeSlide}
                            onReorder={async (orderedIdsByContext) => {
                                try {
                                    await adminReorderSlides(orderedIdsByContext);
                                    toast.success("Slide order saved.");
                                    await reload();
                                } catch (err) {
                                    toast.error(err?.response?.data?.error || "Could not save order.");
                                }
                            }}
                        />
                    )}
                </div>
            )}

            {tab === "posters" && (
                <div className="space-y-6">
                    {/* Tithe & Offertory */}
                    <form onSubmit={handleUploadKey("tithe_poster", "Tithe & Offertory poster")} className="rounded-2xl border border-maroon-100 bg-white/85 p-5 shadow-sm space-y-3">
                        <h3 className="font-serif text-lg text-maroon-800 flex items-center gap-2"><Landmark size={18} className="text-gold-500" /> Tithe &amp; Offertory poster</h3>
                        <p className="text-sm text-maroon-700/80">Appears on the homepage giving section. Upload a new image to swap it out.</p>
                        <input type="file" accept="image/*" onChange={(e) => setPosterFiles((p) => ({ ...p, tithe_poster: e.target.files?.[0] || null }))} data-testid="admin-poster-file" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" required />
                        <button type="submit" disabled={posterBusy === "tithe_poster"} data-testid="admin-poster-submit" className="btn-primary">
                            {posterBusy === "tithe_poster" ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload
                        </button>
                        {tithePoster?.url && tithePoster?.value && (
                            <div className="rounded-lg border border-maroon-100 bg-cream-50 p-3 mt-3">
                                <p className="text-xs uppercase tracking-wider text-gold-500/80 font-semibold mb-2">Currently showing on homepage</p>
                                <img src={tithePoster.url} alt="Current tithe poster" className="w-full max-w-md rounded-lg" />
                            </div>
                        )}
                    </form>

                    {/* Project Annex */}
                    <form onSubmit={handleUploadKey("project_annex", "Project Annex poster")} className="rounded-2xl border border-maroon-100 bg-white/85 p-5 shadow-sm space-y-3">
                        <h3 className="font-serif text-lg text-maroon-800 flex items-center gap-2"><Landmark size={18} className="text-gold-500" /> Project Annex poster</h3>
                        <p className="text-sm text-maroon-700/80">Homepage giving section. Contains Project Annex M-Pesa/pledge details.</p>
                        <input type="file" accept="image/*" onChange={(e) => setPosterFiles((p) => ({ ...p, project_annex: e.target.files?.[0] || null }))} data-testid="admin-annex-file" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" required />
                        <button type="submit" disabled={posterBusy === "project_annex"} data-testid="admin-annex-submit" className="btn-primary">
                            {posterBusy === "project_annex" ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload
                        </button>
                        {annexPoster?.url && annexPoster?.value && (
                            <div className="rounded-lg border border-maroon-100 bg-cream-50 p-3 mt-3">
                                <p className="text-xs uppercase tracking-wider text-gold-500/80 font-semibold mb-2">Currently showing on homepage</p>
                                <img src={annexPoster.url} alt="Current Project Annex poster" className="w-full max-w-md rounded-lg" />
                            </div>
                        )}
                    </form>

                    {/* Calendar poster (image) */}
                    <form onSubmit={handleUploadKey("calendar_poster", "Parish Calendar poster")} className="rounded-2xl border border-maroon-100 bg-white/85 p-5 shadow-sm space-y-3">
                        <h3 className="font-serif text-lg text-maroon-800 flex items-center gap-2"><Calendar size={18} className="text-gold-500" /> Parish Calendar (image)</h3>
                        <p className="text-sm text-maroon-700/80">Shown at the top of Events &amp; Calendar. If both image and PDF are present, image wins.</p>
                        <input type="file" accept="image/*" onChange={(e) => setPosterFiles((p) => ({ ...p, calendar_poster: e.target.files?.[0] || null }))} data-testid="admin-calendar-image-file" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" required />
                        <button type="submit" disabled={posterBusy === "calendar_poster"} data-testid="admin-calendar-image-submit" className="btn-primary">
                            {posterBusy === "calendar_poster" ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload image
                        </button>
                        {calendarPoster?.url && calendarPoster?.value && (
                            <div className="rounded-lg border border-maroon-100 bg-cream-50 p-3 mt-3">
                                <p className="text-xs uppercase tracking-wider text-gold-500/80 font-semibold mb-2">Currently showing on Events &amp; Calendar</p>
                                <img src={calendarPoster.url} alt="Current calendar poster" className="w-full max-w-md rounded-lg" />
                            </div>
                        )}
                    </form>

                    {/* Calendar PDF */}
                    <form onSubmit={handleCalendarPdfUpload} className="rounded-2xl border border-maroon-100 bg-white/85 p-5 shadow-sm space-y-3">
                        <h3 className="font-serif text-lg text-maroon-800 flex items-center gap-2"><FileType size={18} className="text-gold-500" /> Parish Calendar (PDF)</h3>
                        <p className="text-sm text-maroon-700/80">Alternative to the image, shown as a "View Parish Calendar" card that opens the PDF in a new tab. Max 10&nbsp;MB.</p>
                        <input type="file" accept="application/pdf" onChange={(e) => setCalendarPdfFile(e.target.files?.[0] || null)} data-testid="admin-calendar-pdf-file" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" required />
                        <button type="submit" disabled={calendarPdfBusy} data-testid="admin-calendar-pdf-submit" className="btn-primary">
                            {calendarPdfBusy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload PDF
                        </button>
                        {calendarPdf?.url && calendarPdf?.value && (
                            <div className="rounded-lg border border-maroon-100 bg-cream-50 p-3 mt-3">
                                <a href={calendarPdf.url} target="_blank" rel="noreferrer" className="text-sm underline text-maroon-700">
                                    Open current PDF, {calendarPdf.value}
                                </a>
                            </div>
                        )}
                    </form>
                </div>
            )}

            {tab === "account" && (
                <div className="max-w-md">
                    <form onSubmit={handleChangePassword} data-testid="admin-account-form" className="rounded-2xl border border-maroon-100 bg-white/85 p-6 shadow-sm space-y-3">
                        <h3 className="font-serif text-xl text-maroon-800 flex items-center gap-2"><KeyRound size={18} className="text-gold-500" /> Change password</h3>
                        <p className="text-xs text-maroon-700/70">You&apos;ll be signed out automatically, log back in with your new password.</p>
                        <label className="block"><span className="text-xs font-semibold text-maroon-800">Current password</span>
                            <input type="password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} data-testid="admin-pw-current" required autoComplete="current-password" className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                        </label>
                        <label className="block"><span className="text-xs font-semibold text-maroon-800">New password (8+ characters)</span>
                            <input type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} data-testid="admin-pw-new" minLength={8} required autoComplete="new-password" className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                        </label>
                        <label className="block"><span className="text-xs font-semibold text-maroon-800">Confirm new password</span>
                            <input type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} data-testid="admin-pw-confirm" minLength={8} required autoComplete="new-password" className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                        </label>
                        <button type="submit" disabled={pwSaving} data-testid="admin-pw-submit" className="btn-primary w-full justify-center">
                            {pwSaving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Save new password
                        </button>
                    </form>
                </div>
            )}

            {tab === "groups" && <GroupsAdmin groups={groups} onReload={reload} />}
            {tab === "events" && <EventsAdmin events={events} onReload={reload} />}

            {tab === "site-content" && <SiteContentEditor />}
        </div>
    );
};

export default AdminDashboard;

/* ---------------- Groups & SCCs admin ---------------- */
const GroupsAdmin = ({ groups, onReload }) => {
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ description: "", meeting_location: "", moderator_contact: "", vice_moderator_contact: "" });
    const [saving, setSaving] = useState(false);
    const [imgUploading, setImgUploading] = useState(false);
    const [memberUploading, setMemberUploading] = useState(false);

    const startEdit = (g) => {
        setEditing(g);
        setForm({
            description: g.description || "",
            meeting_location: g.meeting_location || "",
            moderator_contact: g.moderator_contact || "",
            vice_moderator_contact: g.vice_moderator_contact || "",
        });
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try { await adminUpdateGroup(editing.id, form); toast.success("Group updated."); setEditing(null); await onReload(); }
        catch (err) { toast.error(err?.response?.data?.error || "Could not save."); }
        finally { setSaving(false); }
    };

    const uploadImage = async (file) => {
        if (!file) return;
        setImgUploading(true);
        try { await adminUploadGroupImage(editing.id, file); toast.success("Icon image updated."); await onReload(); }
        catch (err) { toast.error(err?.response?.data?.error || "Upload failed."); }
        finally { setImgUploading(false); }
    };

    const uploadMemberPhoto = async (file) => {
        if (!file) return;
        setMemberUploading(true);
        try { await adminUploadGroupMemberPhoto(editing.id, file); toast.success("Group photo updated."); await onReload(); }
        catch (err) { toast.error(err?.response?.data?.error || "Upload failed."); }
        finally { setMemberUploading(false); }
    };

    const removeMemberPhoto = async () => {
        if (!window.confirm("Remove the current group photo?")) return;
        setMemberUploading(true);
        try { await adminDeleteGroupMemberPhoto(editing.id); toast.success("Group photo removed."); await onReload(); }
        catch (err) { toast.error(err?.response?.data?.error || "Could not remove."); }
        finally { setMemberUploading(false); }
    };

    const byCategory = { scc: [], ecclesiastical: [], youth: [], ministry: [] };
    for (const g of groups) if (byCategory[g.category]) byCategory[g.category].push(g);

    const catLabel = {
        scc: "Small Christian Communities",
        ecclesiastical: "Ecclesiastical Groups",
        youth: "Youth Groups",
        ministry: "Ministries",
    };

    return (
        <div className="space-y-8">
            <p className="text-sm text-maroon-700/80">
                Click any group to edit its description, meeting location, moderator and vice-moderator contacts, icon image, and Group Photo. The Group Photo appears on the sub-page only.
            </p>
            {["scc", "ecclesiastical", "youth", "ministry"].map((k) => {
                const list = byCategory[k] || [];
                return (
                    <div key={k}>
                        <h3 className="font-serif text-lg mb-3 flex items-center gap-2" style={{ color: "#7A1F2B" }}>
                            {catLabel[k]}
                            <span className="text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5"
                                  style={{ background: "rgba(212, 175, 55, 0.2)", border: "1px solid #D4AF37", color: "#7A1F2B" }}>
                                {list.length}
                            </span>
                        </h3>
                        {list.length === 0 ? (
                            <p className="text-sm italic text-maroon-700/60">No groups seeded in this category yet. Reload the backend on PythonAnywhere to populate ministries.</p>
                        ) : (
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                {list.map((g) => {
                                    const filled = !!(g.description || g.meeting_location || g.moderator_contact);
                                    return (
                                        <button
                                            key={g.id}
                                            type="button"
                                            onClick={() => startEdit(g)}
                                            data-testid={`admin-group-${g.slug}`}
                                            className="text-left rounded-2xl border p-4 transition-all hover:-translate-y-0.5"
                                            style={{
                                                background: "#383838",
                                                borderColor: filled ? "#D4AF37" : "rgba(255,255,255,0.12)",
                                                borderLeftWidth: 4,
                                                borderLeftColor: filled ? "#D4AF37" : "#555555",
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
                                            }}
                                        >
                                            <p className="font-semibold text-sm" style={{ color: "#FFFFFF" }}>{g.name}</p>
                                            <p className="mt-1 text-[10px] uppercase tracking-wider flex items-center gap-1"
                                               style={{ color: filled ? "#D4AF37" : "#CCCCCC" }}>
                                                {filled ? (
                                                    <>
                                                        <CheckCircle2 size={10} /> Details set
                                                    </>
                                                ) : (
                                                    <>
                                                        <Clock size={10} /> Empty, click to fill
                                                    </>
                                                )}
                                            </p>
                                            {g.image_url && (
                                                <p className="mt-1 text-[10px]" style={{ color: "#7dd58a" }}>● Icon uploaded</p>
                                            )}
                                            {g.member_photo_url && (
                                                <p className="mt-0.5 text-[10px]" style={{ color: "#7dd58a" }}>● Group photo uploaded</p>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}

            {editing && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => !saving && setEditing(null)}>
                    <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-cream-50 rounded-2xl p-6 space-y-3" data-testid="admin-group-edit-form">
                        <h3 className="font-serif text-xl text-maroon-800">{editing.name}</h3>
                        <div>
                            <label className="text-xs font-semibold text-maroon-800">Description</label>
                            <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-group-description" className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-maroon-800">Meeting Location</label>
                            <input value={form.meeting_location} onChange={(e) => setForm({ ...form, meeting_location: e.target.value })} data-testid="admin-group-location" className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-maroon-800">Moderator Contact</label>
                            <input value={form.moderator_contact} onChange={(e) => setForm({ ...form, moderator_contact: e.target.value })} data-testid="admin-group-moderator" placeholder="Name and phone/email" className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-maroon-800">Vice-Moderator Contact</label>
                            <input value={form.vice_moderator_contact} onChange={(e) => setForm({ ...form, vice_moderator_contact: e.target.value })} data-testid="admin-group-vice" className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-maroon-800">Patron Saint / Group Icon Image {editing.image_url && "(replace)"}</label>
                            <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="admin-group-image" disabled={imgUploading} className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                            {editing.image_url && <img src={editing.image_url} alt="" className="mt-2 h-32 rounded-lg object-contain bg-black/5" />}
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-maroon-800">Group Photo (shown on sub-page only) {editing.member_photo_url && "(replace)"}</label>
                            <input type="file" accept="image/*" onChange={(e) => uploadMemberPhoto(e.target.files?.[0])} data-testid="admin-group-member-photo" disabled={memberUploading} className="mt-1 w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                            {editing.member_photo_url && (
                                <div className="mt-2 flex items-center gap-3">
                                    <img src={editing.member_photo_url} alt="" className="h-32 rounded-lg object-cover bg-black/5" />
                                    <button type="button" onClick={removeMemberPhoto} disabled={memberUploading} data-testid="admin-group-member-photo-remove" className="text-xs font-semibold text-maroon-700 underline">Remove group photo</button>
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setEditing(null)} className="rounded-full px-4 py-2 text-sm border border-maroon-200 text-maroon-700 hover:bg-maroon-100">Cancel</button>
                            <button type="submit" disabled={saving} data-testid="admin-group-save" className="btn-primary">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

/* ---------------- Events admin ---------------- */
const EventsAdmin = ({ events, onReload }) => {
    const [title, setTitle] = useState("");
    const [dateStr, setDateStr] = useState("");
    const [venue, setVenue] = useState("");
    const [desc, setDesc] = useState("");
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !dateStr) { toast.error("Title and event date are required."); return; }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("title", title.trim());
            fd.append("event_date", dateStr);
            if (venue.trim()) fd.append("venue", venue.trim());
            if (desc.trim())  fd.append("description", desc.trim());
            if (file) fd.append("file", file);
            await adminCreateEvent(fd);
            toast.success("Event created.");
            setTitle(""); setDateStr(""); setVenue(""); setDesc(""); setFile(null);
            await onReload();
        } catch (err) { toast.error(err?.response?.data?.error || "Failed to create."); }
        finally { setSubmitting(false); }
    };

    const removeEvent = async (id) => {
        if (!window.confirm("Delete this event?")) return;
        try { await adminDeleteEvent(id); toast.success("Removed."); await onReload(); }
        catch { toast.error("Could not delete."); }
    };

    return (
        <div className="space-y-6">
            <form onSubmit={submit} className="rounded-2xl border border-maroon-100 bg-white/85 p-5 shadow-sm space-y-3" data-testid="admin-event-form">
                <h3 className="font-serif text-lg text-maroon-800 flex items-center gap-2"><Calendar size={18} className="text-gold-500" /> New event</h3>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="admin-event-title" required className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                <div className="grid gap-3 sm:grid-cols-2">
                    <input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} data-testid="admin-event-date" required className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                    <input type="text" value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="Venue" data-testid="admin-event-venue" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                </div>
                <textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description" data-testid="admin-event-desc" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="admin-event-file" className="w-full rounded-lg border border-maroon-200 bg-white px-3 py-2 text-sm" />
                <button type="submit" disabled={submitting} data-testid="admin-event-submit" className="btn-primary">
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} />} Save event
                </button>
            </form>
            {events.length === 0 ? <Empty>No events yet.</Empty> : (
                <div className="grid gap-3 sm:grid-cols-2">
                    {events.map((e) => (
                        <article key={e.id} data-testid={`admin-event-${e.id}`} className="rounded-2xl border border-maroon-100 bg-cream-50 p-4">
                            {e.poster_url && <img src={e.poster_url} alt="" className="w-full h-40 object-cover rounded-lg" />}
                            <h4 className="font-serif text-maroon-800 mt-2">{e.title}</h4>
                            <p className="text-xs text-maroon-700/70">{formatDate(e.event_date)}{e.venue ? ` · ${e.venue}` : ""}</p>
                            {e.description && <p className="text-sm text-maroon-700/90 mt-2 whitespace-pre-wrap">{e.description}</p>}
                            <button onClick={() => removeEvent(e.id)} data-testid={`admin-event-del-${e.id}`} className="mt-3 inline-flex items-center gap-1 rounded-full border border-maroon-200 text-maroon-700 hover:bg-maroon-100 px-3 py-1.5 text-xs font-semibold">
                                <Trash2 size={14} /> Delete
                            </button>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
};
